import React, { useEffect, useState } from 'react';
import loadable from '@loadable/component';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { AccommodationsDeleteDialog } from '../AccommodationsDeleteDialog';
import { useSortedAccommodationsData } from '../../hooks/useSortedAccommodationsData';
import { EmptyState } from '../EmptyState';
import { SortableCardList } from '../SortableCardList';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { ACCOMMODATIONS_ACTIONS } from '../../Accommodations.constants';
import { ADD_STAY_SEARCH_PARAM_KEY } from '@shared/utils/accommodations';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useTravelPlacesPricingAndBookingDetailsQuery } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { TravelMapBlockType } from '../../Accommodations.types';
import { AutoPopulatedAccommodationsSummary } from '../AutoPopulatedAccommodationsSummary';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useKeycodeListener } from '@shared/utils/hooks/useKeycodeListener';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { AsyncAutoPopulationStatus } from '../AsyncAutoPopulationStatus';
import { AccommodationPlaceType } from '@graphql/generated';
import { useHistory, useRouterHelper, useTranslation } from '@shared/core';
import { PlanningRoutes } from '@apps/admin/routes/Planning/Planning.routes';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { NonJoyBookingsWarning } from '../NonJoyBookingsWarning/NonJoyBookingsWarning';

// Delays loading of the AiRecommendationsFeedback component until it is needed, Also reduces initial bundle size for the AccommodationsList component
const AiRecommendationsFeedback = loadable(
  () => import(/* webpackChunkName: "admin/travel-map/ai-recommendations-feedback" */ '../AiRecommendationsFeedback/AiRecommendationsFeedback'),
);

const EDIT_HOTEL_ID_KEY = 'addMatchedHotel';
const KEY_CODE_H = 72;

export const AccommodationsList: React.FC = () => {
  const { customComponentBlockData } = useCustomComponentStateContext();
  const { navigateToScreen } = useNavigationPaneContext();
  const { accommodationsData, isSuperAdmin, componentData, pageSlug } = useAccommodationsContext();

  const history = useHistory();
  const router = useRouterHelper();
  const { tq } = useTranslation('websiteEditor');
  const telemetry = useAdminAccommodationsTelemetry();

  const handleExploreHotelsClick = useEventCallback(() => {
    telemetry.addAccommodationItemClicked('accommodations', {
      label: 'Add Hotel',
      itemType: TravelMapBlockType.travelMapAccommodationsHotel,
    });

    history.push(router.buildPath(`${PlanningRoutes.accommodations}/browse`));
  });

  const nonJoyHotelsCount = React.useMemo(() => {
    return accommodationsData?.accommodationPlacesV2?.filter(place => place.type !== AccommodationPlaceType.hotel).length || 0;
  }, [accommodationsData]);

  const { accommodationHotels, blockOrdering, autoPopulatedHotels } = useSortedAccommodationsData({
    accommodations: accommodationsData,
    blockOrderingInitial: componentData.blockOrdering,
  });
  const updateSearchParam = useUpdateSearchParam();
  const queryParamHelper = useQueryParamHelper();
  const addMatchedHotel = queryParamHelper.getValueString(EDIT_HOTEL_ID_KEY);
  const addStay = queryParamHelper.getValueString(ADD_STAY_SEARCH_PARAM_KEY);

  useEffect(() => {
    if (addMatchedHotel === 'true') {
      navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_HOTEL_ADD);
      updateSearchParam(EDIT_HOTEL_ID_KEY, null);
    }
  }, [addMatchedHotel, navigateToScreen, updateSearchParam, accommodationsData]);

  // Check if addStay search param exists, navigate to accommodations add screen and clear param
  useEffect(() => {
    if (addStay === 'true') {
      navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_ADD);
      updateSearchParam(ADD_STAY_SEARCH_PARAM_KEY, null);
    }
  }, [addStay, navigateToScreen, updateSearchParam]);

  const { loading: loadingEventInfo, eventInfo } = useEventInfo();
  const [autoPopulateAiNotesEnabled] = useIsFeatureEnabled('travelAutoPopulatedAiNotesSurfaced');
  const [asyncHotelAutopopulationEnabled] = useIsFeatureEnabled('asyncHotelAutopopulationEnabled');

  const canViewAutoPopulateInsights = Boolean(autoPopulateAiNotesEnabled && eventInfo?.eventId);
  const [showAutoPopulateInsights, setShowAutoPopulateInsights] = useState(false);
  const toggleAutoPopulateInsights = useEventCallback(() => {
    if (!canViewAutoPopulateInsights) {
      return;
    }
    setShowAutoPopulateInsights(prev => !prev);
  });

  useKeycodeListener([KEY_CODE_H], toggleAutoPopulateInsights, { ctrlKey: true, shiftKey: true }, [toggleAutoPopulateInsights]);

  useEffect(() => {
    if (!canViewAutoPopulateInsights && showAutoPopulateInsights) {
      setShowAutoPopulateInsights(false);
    }
  }, [canViewAutoPopulateInsights, showAutoPopulateInsights]);

  const accommodationPlaceIds = accommodationHotels
    .filter(accommodation => accommodation.type !== TravelMapBlockType.travelMapAccommodationsRoomblock)
    .map(accommodation => accommodation.id);

  if (!accommodationHotels.length) {
    return (
      <>
        {asyncHotelAutopopulationEnabled && <AsyncAutoPopulationStatus />}
        <EmptyState
          buttonText={tq`Explore Hotels`}
          imageAltText={tq`Guests Bookings`}
          onButtonClick={handleExploreHotelsClick}
          title={tq`Add hotel recommendations to save guests time and money`}
          imageSrc="https://withjoy.com/assets/public/travel/admin-accommodations-empty.png"
          subtitle={tq`Give your guests the best rates on hotels close to your venue, available exclusively to Joy customers.`}
        >
          {nonJoyHotelsCount > 0 && <NonJoyBookingsWarning nonJoyHotelsCount={nonJoyHotelsCount} />}
        </EmptyState>
      </>
    );
  }

  return (
    <>
      {asyncHotelAutopopulationEnabled && <AsyncAutoPopulationStatus />}
      <AutoPopulatedAccommodationsSummary
        accommodationsCount={accommodationHotels.length}
        autoPopulatedHotelsCount={autoPopulatedHotels.length}
      />
      <SortableCardList
        accommodationHotels={accommodationHotels}
        blockOrdering={blockOrdering}
        showAutoPopulateInsights={showAutoPopulateInsights && canViewAutoPopulateInsights}
      />
      <AiRecommendationsFeedback />
      {customComponentBlockData && (
        <AccommodationsDeleteDialog
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          id={(customComponentBlockData as any).id}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          type={(customComponentBlockData as any).type}
          showCloseIcon
          pageSlug={pageSlug}
          isSuperAdmin={isSuperAdmin}
        />
      )}
      {!loadingEventInfo && <RequestAccommodationPlacePricing accommodationPlaceIds={accommodationPlaceIds} />}
    </>
  );
};

const RequestAccommodationPlacePricing = ({ accommodationPlaceIds }: { accommodationPlaceIds: string[] }): null => {
  useTravelPlacesPricingAndBookingDetailsQuery(accommodationPlaceIds, 'accommodationPlace');
  return null;
};
